<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { api, ApiError } from '../../lib/api';
  import { user, type User } from '../../lib/stores/auth';
  import { showToast } from '../../lib/stores/toast';
  import { withPageTitle } from '../../lib/page-title';
  import {
    fetchAvailability,
    hintFor,
    normalize,
    type AvailabilityState,
  } from '../../lib/username';
  import Button from '../../components/ui/Button.svelte';
  import Card from '../../components/ui/Card.svelte';
  import Field from '../../components/ui/Field.svelte';
  import Banner from '../../components/ui/Banner.svelte';

  $effect(() => withPageTitle('Pick a username'));

  // OAuth-finish step: the user came back from Google's consent screen,
  // GoogleController stashed their profile in session('oauth_pending'),
  // and they need to pick a username before the account is created.
  //
  // POST /api/auth/oauth/finish reads the session, creates the user, and
  // returns the same UserResource shape as login/register. A 410 response
  // means the session expired or was lost — back to /register cleanly.

  let username = $state('');
  let submitting = $state(false);
  let serverError = $state<ApiError | null>(null);

  let availability = $state<AvailabilityState>({ status: 'idle' });
  let availabilityToken = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  function onUsernameInput(): void {
    if (debounceTimer) clearTimeout(debounceTimer);
    const value = normalize(username);
    if (value !== username) username = value;
    availability = { status: 'checking' };
    const myToken = ++availabilityToken;
    debounceTimer = setTimeout(async () => {
      const next = await fetchAvailability(value);
      if (myToken === availabilityToken) availability = next;
    }, 350);
  }

  const usernameHint = $derived(hintFor(availability));
  const usernameLooksBad = $derived(availability.status === 'unavailable');

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    if (submitting) return;
    submitting = true;
    serverError = null;
    try {
      const res = await api<{ user: User }>('/api/auth/oauth/finish', {
        method: 'POST',
        body: JSON.stringify({ username }),
      });
      user.set(res.user);
      navigate('/account', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) {
        showToast('Your sign-in session expired. Please try again.', 'warn');
        navigate('/register', { replace: true });
        return;
      }
      if (err instanceof ApiError && err.status === 409) {
        showToast('That email is already registered. Please sign in.', 'info');
        navigate('/login', { replace: true });
        return;
      }
      if (err instanceof ApiError) {
        serverError = err;
      } else {
        showToast("Couldn't finish signup. Please try again.", 'error');
      }
    } finally {
      submitting = false;
    }
  }
</script>

<Card padding="lg" elevated>
  <form class="auth-form" onsubmit={submit} novalidate>
    <header class="auth-form__header">
      <h2 class="auth-form__title">Pick a username</h2>
      <p class="auth-form__subtitle">
        We'll use this on leaderboards and your public profile.
      </p>
    </header>

    {#if serverError && !serverError.errors}
      <Banner variant="error">{serverError.message}</Banner>
    {/if}

    <Field
      label="Username"
      name="username"
      autocomplete="username"
      required
      bind:value={username}
      oninput={onUsernameInput}
      hint={usernameHint}
      error={serverError?.fieldError('username') ?? (usernameLooksBad ? 'Not available.' : null)}
    />

    <Button type="submit" variant="primary" size="lg" block loading={submitting}>
      Finish signup
    </Button>
  </form>
</Card>
